import { Component, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService, LoginCredentials } from '@core/services/auth.service';
import { catchError, EMPTY, finalize, Observable } from 'rxjs';

import { Router } from '@angular/router';
import { InputNavigationDirective } from '@core/directives';
import { BaseUser } from '@core/models';
import { LoggerService } from '@core/services/logger.service';
import { ForgotPasswordComponent } from '@ui/components/forgot-password.component';
import { ControlsOf } from '@wizard/models';
import { Button } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Password } from 'primeng/password';
import { ModificaPasswordComponent } from '@ui/components/modifica-password/modifica-password.component';

/**
 * Componente riusabile contenente esclusivamente il form di login e la sua logica.
 * Il layout esterno viene delegato a wrapper differenti (front-office, back-office, ecc.).
 */
@Component({
  selector: 'fl-login-form',
  imports: [
    ReactiveFormsModule,
    Button,
    InputText,
    Password,
    FloatLabel,
    Message,
    ForgotPasswordComponent,
    InputNavigationDirective,
    ModificaPasswordComponent,
  ],
  template: `
    @if (mode() === 'login') {
      <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-8">
        <div class="flex flex-col gap-1">
          <p-float-label variant="on">
            <input pInputText id="username" formControlName="username" class="w-full" />
            <label for="username">Username</label>
          </p-float-label>
          @if (loginForm.controls['username'].touched && loginForm.controls['username'].invalid) {
            <p-message severity="error" size="small" variant="simple">Username obbligatorio</p-message>
          }
        </div>

        <div class="flex flex-col gap-1">
          <p-float-label variant="on">
            <p-password
              inputId="password"
              feedback="false"
              fluid
              [toggleMask]="true"
              formControlName="password"
              (keydown.enter)="onSubmit()" />
            <label for="password">Password</label>
          </p-float-label>
          @if (loginForm.controls['password'].touched && loginForm.controls['password'].invalid) {
            <p-message severity="error" size="small" variant="simple">Password obbligatoria</p-message>
          }
        </div>

        <div class="flex flex-col justify-end mt-2">
          <p-button
            styleClass="w-full"
            type="submit"
            label="Accedi"
            [loading]="loading()"
            severity="primary"
            [disabled]="!loginForm.valid"></p-button>
        </div>

        @if (showLinks() && mode() === 'login') {
          <div class="flex flex-col gap-2 items-center">
            @if (enableForgot()) {
              <p-button
                label="Password dimenticata?"
                [link]="true"
                (click)="onForgotPassword()"
                styleClass="text-sm font-medium text-gray-600 hover:text-gray-800 hover:underline"
                [disabled]="loading()" />
            }
            @if (enableRegister()) {
              <p-button
                label="Non hai un account? Registrati"
                [link]="true"
                (click)="onRegister()"
                styleClass="text-sm font-medium text-gray-600 hover:text-gray-800 hover:underline"
                [disabled]="loading()" />
            }
          </div>
        }
      </form>
    }
    @if (mode() === 'forgot-password') {
      <fl-forgot-password (backToLogin)="onBackToLogin()" />
    }
    @if (mode() === 'change-password') {
      <fl-modifica-password (closeEvent)="onBackToLogin()" [username]="username()!"></fl-modifica-password>
    }
  `,
})
export class LoginFormComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthenticationService);
  private logger = inject(LoggerService);
  private router = inject(Router);

  enableRegister = input<boolean>(true);
  enableForgot = input<boolean>(true);
  showLinks = input<boolean>(true);

  loginType = input<'richiedente' | 'operatore'>('richiedente');

  register = output<void>();
  login = output<void>();

  loading = signal(false);
  mode = signal<'login' | 'forgot-password' | 'change-password'>('login');
  username = signal<string | undefined>(undefined);

  loginForm = this.fb.group<ControlsOf<LoginCredentials>>({
    username: this.fb.nonNullable.control('', [Validators.required]),
    password: this.fb.nonNullable.control('', [Validators.required]),
  });

  constructor() {
    effect(() => {
      this.loginForm.enable();
      if (this.loading()) {
        this.loginForm.disable();
      }
    });
  }

  onSubmit() {
    if (this.loginForm.invalid) return;
    this.loading.set(true);
    const credentials = this.loginForm.value as LoginCredentials;
    const login$ = (
      this.loginType() === 'richiedente'
        ? this.authService.loginRichiedente(credentials)
        : this.authService.loginOperatore(credentials)
    ) as Observable<BaseUser>;

    login$
      .pipe(
        finalize(() => this.loading.set(false)),
        catchError(err => {
          if (err.error?.error === 'password_expired') this.changePassword();
          return EMPTY;
        })
      )
      .subscribe((user: BaseUser) => {
        if (user) this.login.emit();
      });
  }

  onForgotPassword() {
    this.mode.set('forgot-password');
  }

  onRegister() {
    this.register.emit();
  }

  onBackToLogin() {
    this.mode.set('login');
  }

  private changePassword() {
    this.username.set(this.loginForm.value.username);
    this.mode.set('change-password');
  }
}
